import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Table from 'apollo-react/components/Table';
import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { ActionCell, FieldCell } from './TableCells';
import { useFormSelector } from '../../../../../custom-hooks';
import { useTranslation } from '../../../../../custom-hooks/useTranslation';
import { ENTITY_MODEL_EDITOR, EXPRESSION_EDITOR } from '../../../../constants/editor.constants';
import { getEntityDefinition, getTranslations } from '../../../../selectors/common.selectors';
import { getPropTranslation } from '../../../../utils/form-manager.utils';
import TablePropertiesModal from '../TablePropertiesModal';

const useStyles = makeStyles({
  root: {
    overflow: 'hidden',
    '& [class*="Table-tableWrapper"]': {
      overflow: 'hidden',
    },
    '& [class*="MuiToolbar-root"]': {
      display: 'flex',
      justifyContent: 'flex-start',
    },
    '& [class*="MuiToolbar-root"] div': {
      paddingRight: 20,
    },
    '& [class*="MuiTableHead-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="MuiTableBody-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="InternalTableBody-editMode"]': {
      backgroundColor: 'white',
      borderTop: 'none !important',
      borderBottom: 'none !important',
    },
    '& [class*="EmptyRow-middleAlign"]': {
      height: '90px !important',
    },

    '& [class*="EmptyRow-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
      minHeight: '239px',
    },
    '& [class*="MuiFormControl-marginNormal"]': {
      marginTop: '0px',
    },
  },
});

const columns = [
  {
    header: 'Field',
    accessor: 'field',
    customCell: FieldCell,
  },
  {
    header: 'Label',
    accessor: 'labelToDisplay',
    customCell: FieldCell,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
  },
];

const FieldsSelectionModal = ({ controlId, name, currentControlModel, setCurrentControlModel }) => {
  const currentPropertyValue = currentControlModel[name];
  const classes = useStyles();
  const translateString = useTranslation();

  const [isTablePropertiesModalOpen, setIsTablePropertiesModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [rows, setRows] = useState(currentPropertyValue ?? []);

  const entityDefinition = useFormSelector(getEntityDefinition);

  const translations = useFormSelector(getTranslations);

  const currentKey = useRef('');

  useEffect(() => {
    const modifyRows = currentPropertyValue?.map(
      ({ field, id, label = '', labelType = 'formula' }) => {
        const fieldLabel = getPropTranslation(entityDefinition, translations, field);
        const translatedFieldName = fieldLabel && field ? `${fieldLabel} (${field})` : '';
        const labelToDisplay = label.startsWith('eval||') ? label.split('eval||')[1] : label;

        return {
          id,
          field,
          translatedFieldName,
          label,
          labelType,
          labelToDisplay,
        };
      }
    );

    setRows(modifyRows ? modifyRows : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPropertyValue]);

  const updateLocalState = (newRowsTemp) => {
    const newRows = newRowsTemp || rows;

    if (controlId && name) {
      const controlModel = {
        ...currentControlModel,
        [name]: newRows.length
          ? newRows.map(({ field, id, labelToDisplay = '', labelType = 'formula' }) => {
              const label =
                labelToDisplay && labelType === 'formula'
                  ? `eval||${labelToDisplay}`
                  : labelToDisplay;

              return {
                field,
                id,
                label,
                labelType,
              };
            })
          : undefined,
      };
      setCurrentControlModel(controlModel);
      setRows(newRows);
    }
  };

  const addRow = () => {
    const rowId = `${uuidv4()}`;
    const newRow = {
      id: rowId,
      field: '',
      translatedFieldName: '',
      label: '',
      labelType: 'formula',
      labelToDisplay: '',
    };

    updateLocalState([...rows, newRow]);
  };

  const deleteRow = (id) => {
    const newRows = rows.filter((row) => row.id !== id);
    updateLocalState(newRows);
  };

  const editRow = (id, key, value = '') => {
    const updatedRows = rows.map((row) => (row.id === id ? { ...row, [key]: value } : row));
    updateLocalState(updatedRows);
  };

  const bulkEditRow = (id, changes) => {
    const updatedRows = rows.map((row) => (row.id === id ? { ...row, ...changes } : row));
    updateLocalState(updatedRows);
  };

  const handleOpenTablePropertiesModal = (key, row) => {
    currentKey.current = key;
    setIsTablePropertiesModalOpen(true);
    setSelectedRow({ ...row, currentKey: key });
  };

  const CustomHeader = () => {
    const buttonText = translateString('Add New Field');
    return (
      <div>
        <Button variant="text" size="small" onClick={addRow}>
          {buttonText}
        </Button>
      </div>
    );
  };

  const getModalType = (key) => (key === 'field' ? ENTITY_MODEL_EDITOR : EXPRESSION_EDITOR);

  const handleUpdate = (id, key, value) => {
    if (key === 'field') {
      const changes = { [key]: value, labelToDisplay: '' };
      bulkEditRow(id, changes);
    } else {
      editRow(id, key, value);
    }
  };

  return (
    <div className={classes.root}>
      <Table
        title={translateString('Selected Fields')}
        columns={columns}
        initialSortOrder="asc"
        hidePagination
        rows={rows.map((row) => ({
          ...row,
          rows,
          key: row.id,
          editRow,
          deleteRow,
          setSelectedRow,
          handleOpenTablePropertiesModal,
        }))}
        CustomHeader={CustomHeader}
      />
      {isTablePropertiesModalOpen && (
        <TablePropertiesModal
          type={getModalType(selectedRow.currentKey)}
          row={selectedRow}
          handleUpdate={handleUpdate}
          onClose={() => setIsTablePropertiesModalOpen(false)}
        />
      )}
    </div>
  );
};

export default FieldsSelectionModal;
